import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Microphone, Waveform, SlidersHorizontal, MusicNotes, Export, Sparkle, ArrowRight, ArrowUpRight, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";

// Framer-motion configs hoisted to module scope so their object identity is
// stable across renders — avoids the reconciler re-evaluating equal props and
// keeps the animation machinery from re-arming. Only truly static configs
// live here; per-instance transitions (with unique delays) stay inline.
const FADE_UP_HERO_INITIAL = { opacity: 0, y: 12 };
const FADE_UP_SMALL_INITIAL = { opacity: 0, y: 18 };
const FADE_UP_BIG_INITIAL = { opacity: 0, y: 24 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE = { once: true };

const features = [
  {
    icon: Microphone, title: "Vocal capture",
    body: "One mic, zero setup. Beatbox, hum, sing — we record it raw.",
    testid: "feature-voice-in",
    img: "https://images.unsplash.com/photo-1453738773917-9c3eff1db985?q=80&w=1200&auto=format&fit=crop",
    alt: "Vocalist singing into a studio microphone with pop filter",
  },
  {
    icon: Waveform, title: "Rhythm decoder",
    body: "Every vocal hit becomes a drum loop, quantized and ready.",
    testid: "feature-rhythms",
    img: "https://images.unsplash.com/photo-1571512379940-716326f35dbd?q=80&w=1200&auto=format&fit=crop",
    alt: "Roland TR-808 rhythm composer drum machine, close-up",
  },
  {
    icon: MusicNotes, title: "Melody mapper",
    body: "Hums turn into clean synth and piano layers.",
    testid: "feature-melody",
    img: "https://images.unsplash.com/photo-1733900606117-2522d8ed3a7b?q=80&w=1200&auto=format&fit=crop",
    alt: "Close-up of an analog synthesizer with piano keys and knobs",
  },
  {
    icon: SlidersHorizontal, title: "Multi-track mixer",
    body: "Layer, mute, solo, balance — like a proper DAW.",
    testid: "feature-multitrack",
    img: "https://images.unsplash.com/photo-1520804454307-f3755438375a?q=80&w=1200&auto=format&fit=crop",
    alt: "Grayscale close-up of a large mixing console with faders",
  },
  {
    icon: Sparkle, title: "Sound Guy assistant",
    body: "An in-app helper that shows you exactly how every tool works.",
    testid: "feature-ai",
    img: "https://images.unsplash.com/photo-1522870389523-7e83c0065eaf?q=80&w=1200&auto=format&fit=crop",
    alt: "Akai MPK mini launchpad with glowing pads suggesting AI-driven beats",
  },
  {
    icon: Export, title: "Studio export",
    body: "Render WAV or MP3. Share a link. Done.",
    testid: "feature-export",
    img: "https://images.unsplash.com/photo-1483000805330-4eaf0a0d82da?q=80&w=1200&auto=format&fit=crop",
    alt: "Shallow focus close-up of audio mixer knobs during final mastering",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#C7C9CC] text-[#0A0A0A]">
      {/* Nav */}
      <header className="absolute top-0 left-0 right-0 z-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tighter" data-testid="brand-logo">
            BEAT BOX
          </Link>
          <nav className="hidden md:flex items-center gap-10 text-sm">
            <a href="#features" className="hover:opacity-60 transition-opacity">Features</a>
            <a href="#how" className="hover:opacity-60 transition-opacity">How it works</a>
            <a href="#cta" className="hover:opacity-60 transition-opacity">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={() => nav("/dashboard")}
                data-testid="nav-dashboard-btn"
                className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-11 px-6 font-semibold"
              >
                Open studio <ArrowRight className="ml-2" size={16} weight="bold" />
              </Button>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login-link"
                  className="text-sm font-medium hover:opacity-60 transition-opacity"
                >
                  Log in
                </Link>
                <Button
                  onClick={() => nav("/register")}
                  data-testid="nav-signup-btn"
                  className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-11 px-6 font-semibold"
                >
                  Start free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background photo — full-bleed on every viewport. Desktop keeps the
            grey-to-transparent gradient fade for left-side text legibility;
            mobile uses a soft top+bottom scrim so the centered text stack over
            the face stays readable without hiding the photo. */}
        <div className="absolute inset-0 z-0" aria-hidden>
          <img
            src="/img/hero-bg.jpg"
            alt=""
            className="w-full h-full object-cover object-right"
          />
          {/* Desktop gradient fade to the left. */}
          <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-[#C7C9CC] via-[#C7C9CC]/70 to-transparent" />
          {/* Mobile scrim — photo fades to solid black at the bottom so the
              white headline/description text stays legible over it. */}
          <div className="md:hidden absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/10 via-[#0A0A0A]/55 to-[#0A0A0A]" />
        </div>

        {/* Massive wordmark — desktop keeps original left-justified layout;
            mobile is bigger and center-aligned over the face. */}
        <div className="absolute inset-0 z-[1] flex items-start md:items-center justify-center md:justify-start pt-28 md:pt-0 pointer-events-none select-none">
          <h1
            className="bb-massive text-[32vw] md:text-[22vw] lg:text-[18vw] text-white leading-[0.8] md:leading-[0.78] text-center md:text-left md:ml-[28vw] lg:ml-[26vw] drop-shadow-[0_6px_20px_rgba(0,0,0,0.25)] md:drop-shadow-none"
            aria-hidden
          >
            BEAT<br />BOX
          </h1>
        </div>

        {/* Headline + description — mobile: centered vertically & horizontally
            above the green wave; desktop: original two-position floating layout. */}
        <div className="md:hidden absolute inset-x-0 z-10 px-6 text-center" style={{ top: "62%", transform: "translateY(-50%)" }}>
          <h2
            className="font-display text-3xl text-white tracking-tighter leading-[0.95]"
            data-testid="hero-headline"
          >
            The voice-first<br />music studio.
          </h2>
          <motion.p
            initial={FADE_UP_HERO_INITIAL}
            animate={FADE_UP_ANIMATE}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            data-testid="hero-tagline"
            className="mt-4 mx-auto max-w-[320px] text-sm text-white/85 leading-snug"
          >
            Beatbox, hum, sing — and Beat Box turns your voice into drums, melodies and full tracks. Browser-based. AI-assisted.
          </motion.p>
        </div>

        {/* Desktop-only floating tagline + description — single right-anchored
            column so headline and body share the same right edge, with a
            10 px gap between them. Right edge sits 10 px left of the giant
            BEAT BOX wordmark (which starts at 26vw on lg, 28vw on md). */}
        <div
          className="hidden md:flex absolute z-10 left-6 lg:left-12 md:right-[calc(72vw+10px)] lg:right-[calc(74vw+10px)] flex-col items-end text-right pointer-events-none"
          style={{ top: "50%", transform: "translateY(calc(-50% - 4vw))" }}
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-[3rem] text-[#0A0A0A] tracking-tighter leading-[0.95]">
            The<br />Voice-First<br />Music Studio
          </h2>
          <motion.p
            initial={FADE_UP_HERO_INITIAL}
            animate={FADE_UP_ANIMATE}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="mt-[10px] text-base lg:text-lg text-[#2B2B2B] leading-snug"
          >
            Beatbox, hum, sing — and Beat Box turns your voice into drums, melodies and full tracks. Browser-based. AI-assisted.
          </motion.p>
        </div>

        {/* Glowing green cable accent — mobile: stretched full-width across the
            bottom band, sitting just above the button row. Desktop: original
            left-half curve. */}
        <svg
          className="absolute left-0 right-0 md:right-auto bottom-24 md:bottom-0 w-full md:w-[55vw] pointer-events-none z-[2]"
          viewBox="0 0 600 220"
          preserveAspectRatio="none"
          fill="none"
          aria-hidden
        >
          <path
            d="M-20 200 C 80 200, 140 80, 260 110 S 460 200, 620 130"
            stroke="#A8FF1F"
            strokeWidth="10"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 0 10px rgba(168,255,31,0.7))" }}
          />
        </svg>

        {/* CTA row — mobile: horizontal, centered, under the green wave.
            Desktop: pinned to the right endpoint of the wave (original). */}
        <div
          className="absolute z-10 inset-x-0 md:inset-x-auto md:left-[54vw] bottom-8 md:bottom-[5vw] flex flex-row flex-wrap items-center justify-center md:justify-start gap-3 px-4 md:px-0"
          data-testid="hero-cta-row"
        >
            <Button
              data-testid="hero-cta-start"
              onClick={() => nav(user ? "/dashboard" : "/register")}
              className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-12 md:h-14 px-6 md:px-8 text-sm md:text-base font-semibold"
            >
              <Microphone className="mr-2" size={18} weight="fill" /> Start recording
            </Button>
            <Button
              data-testid="hero-cta-demo"
              onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
              variant="ghost"
              className="bb-pill border-2 border-[#A8FF1F] hover:bg-[#A8FF1F]/10 text-white hover:text-white h-12 md:h-14 px-5 md:px-6 text-sm md:text-base font-semibold"
            >
              How it works <ArrowRight className="ml-2" size={16} weight="bold" />
            </Button>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-[#B0B3B7] bg-[#C7C9CC] py-3 overflow-hidden">
        <div className="bb-marquee inline-flex gap-12 whitespace-nowrap font-display text-2xl text-[#0A0A0A]/40 tracking-tighter">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={`mq-${i}`} className="inline-flex items-center gap-12">
              BOOM · TS · CLAP · TS <span className="text-[#A8FF1F] [text-shadow:0_0_18px_rgba(168,255,31,0.5)]">●</span> BEAT BOX <span className="text-[#A8FF1F] [text-shadow:0_0_18px_rgba(168,255,31,0.5)]">●</span> HUM · LA · LA
            </span>
          ))}
        </div>
      </section>

      {/* TUNE WITH TOTAL CONTROL — feature spotlight (split asymmetric like ref) */}
      <section id="features" className="bg-[#E2E3E5]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="mb-16 max-w-3xl text-left">
            <span className="bb-tag" data-testid="features-overline">TUNE WITH TOTAL CONTROL</span>
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-6">
              Your voice<br />is the instrument.
            </h3>
            <p className="text-[#2B2B2B] mt-6 text-base lg:text-lg">
              Beat Box analyses your vocal hits, your hummed melodies, and your improvised takes — then turns them into clean, mixable tracks you can layer, edit and export.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Big spotlight card */}
            <motion.div
              initial={FADE_UP_BIG_INITIAL}
              whileInView={FADE_UP_ANIMATE}
              viewport={VIEWPORT_ONCE}
              transition={{ duration: 0.6 }}
              className="col-span-12 md:col-span-7 bb-card overflow-hidden relative min-h-[420px] bg-[#0A0A0A]"
              data-testid="tune-spotlight-card"
            >
              <img
                src="/img/dashboard.jpg"
                alt="Beat Box studio: multi-track timeline with TR-808 and TR-909 drum layers, editable notes and volume sliders"
                className="absolute inset-0 w-full h-full object-cover object-top"
                data-testid="tune-spotlight-image"
              />
              {/* Bottom gradient — anchors the caption without washing out the UI screenshot. */}
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 via-black/50 to-transparent" />
              <div className="absolute top-6 left-6">
                <span className="bb-tag bg-white/85 backdrop-blur">01 · STUDIO</span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h4 className="font-display text-3xl md:text-4xl tracking-tighter">Multi-track in your browser.</h4>
                <p className="text-white/85 text-sm mt-2 max-w-md">
                  Layer rhythm, melody and vocal tracks. Mute, solo, slide volume — all on a real timeline.
                </p>
              </div>
            </motion.div>

            {/* Side stacked cards */}
            <div className="col-span-12 md:col-span-5 grid grid-cols-1 gap-6">
              <motion.div
                initial={FADE_UP_BIG_INITIAL}
                whileInView={FADE_UP_ANIMATE}
                viewport={VIEWPORT_ONCE}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bb-card p-8 flex flex-col gap-3 min-h-[195px] bg-[#A8FF1F] text-[#0A0A0A] border-[#A8FF1F]"
              >
                <span className="bb-tag w-fit border-[#0A0A0A]/40 text-[#0A0A0A] bg-[#0A0A0A]/5">02 · DRUMIFY</span>
                <h4 className="font-display text-2xl md:text-3xl tracking-tighter">Vocal beats → real drums.</h4>
                <p className="text-[#0A0A0A]/80 text-sm">
                  Onset-detected hits get replaced with kick, snare and hat samples in one tap.
                </p>
              </motion.div>
              <motion.div
                initial={FADE_UP_BIG_INITIAL}
                whileInView={FADE_UP_ANIMATE}
                viewport={VIEWPORT_ONCE}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bb-card p-8 flex flex-col gap-3 min-h-[195px] bg-[#0A0A0A] text-white border-[#0A0A0A]"
              >
                <span className="bb-tag w-fit border-white/30 text-white/80 bg-white/5">03 · SOUND GUY</span>
                <h4 className="font-display text-2xl md:text-3xl tracking-tighter">Your in-app copilot.</h4>
                <p className="text-white/75 text-sm">
                  Ask how any tool works, how to layer a take, or when to Sync vs. Snap. Real answers, no fluff.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SPECIAL OFFER strip */}
      <section className="bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-14 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-7 text-left">
            <span className="bb-tag border-white/30 text-white/80 bg-white/5">FREE WHILE IN BETA</span>
            <h3 className="font-display text-4xl md:text-5xl tracking-tighter mt-6 leading-[0.95]">
              Unlimited projects.<br />Zero credit card.
            </h3>
            <p className="text-white/75 mt-4 text-base lg:text-lg max-w-xl">
              Make as many beats as your throat can handle. We&rsquo;ll never ask for a card while Beat Box is in beta.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 flex md:justify-end">
            <Button
              onClick={() => nav(user ? "/dashboard" : "/register")}
              data-testid="cta-strip-btn"
              className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-14 px-8 text-base font-semibold"
            >
              Claim my studio <ArrowUpRight className="ml-2" size={18} weight="bold" />
            </Button>
          </div>
        </div>
        {/* Subtle wave decoration */}
        <svg className="absolute right-0 top-0 h-full w-[40%] opacity-30 pointer-events-none" viewBox="0 0 400 200" fill="none" aria-hidden>
          <path d="M0 100 Q 50 30, 100 100 T 200 100 T 300 100 T 400 100" stroke="#A8FF1F" strokeWidth="2" />
          <path d="M0 120 Q 50 50, 100 120 T 200 120 T 300 120 T 400 120" stroke="#A8FF1F" strokeWidth="2" />
          <path d="M0 80 Q 50 10, 100 80 T 200 80 T 300 80 T 400 80" stroke="#A8FF1F" strokeWidth="2" />
        </svg>
      </section>

      {/* CUTTING-EDGE FEATURES grid */}
      <section className="bg-[#C7C9CC]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="mb-12 max-w-3xl text-left">
            <span className="bb-tag">CUTTING-EDGE</span>
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-6">
              Everything you need.<br />Nothing you don&rsquo;t.
            </h3>
            <p className="text-[#2B2B2B] mt-6 text-base lg:text-lg">
              A focused toolkit — six features that turn a voice memo into a finished, shareable track without any plugins to install.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={FADE_UP_SMALL_INITIAL}
                whileInView={FADE_UP_ANIMATE}
                viewport={VIEWPORT_ONCE}
                transition={{ delay: i * 0.05 }}
                className="bb-card group hover:-translate-y-1 hover:border-[#A8FF1F] transition-all duration-300 overflow-hidden flex flex-col bg-[#0A0A0A] text-white border-[#0A0A0A]"
                data-testid={f.testid}
              >
                {/* Topical image strip — fills what used to be the top white space.
                    Subtle gradient at the bottom eases the transition into the card body. */}
                <div className="relative h-44 lg:h-48 overflow-hidden">
                  <img
                    src={f.img}
                    alt={f.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                    data-testid={`${f.testid}-image`}
                  />
                  <div
                    className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.9) 100%)" }}
                  />
                </div>
                {/* Content — icon floats up over the image/white boundary. */}
                <div className="relative px-8 pb-8 -mt-6 flex-1 flex flex-col">
                  <div className="w-12 h-12 rounded-full bg-[#A8FF1F] flex items-center justify-center mb-5 shadow-[0_6px_20px_rgba(168,255,31,0.4)] group-hover:scale-110 transition-transform">
                    <f.icon size={22} weight="bold" className="text-[#0A0A0A]" />
                  </div>
                  <h4 className="font-display text-2xl tracking-tighter mb-2 text-white">{f.title}</h4>
                  <p className="text-white/70 text-sm leading-relaxed">{f.body}</p>
                  <div className="mt-6 text-white/40 group-hover:text-[#A8FF1F] transition-colors">
                    <ArrowUpRight size={20} weight="bold" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — three steps */}
      <section id="how" className="bg-[#E2E3E5]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="mb-16 max-w-3xl text-left">
            <span className="bb-tag">FROM MIC TO MIX</span>
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-6">
              Three steps.<br />One banger.
            </h3>
            <p className="text-[#2B2B2B] mt-6 text-base lg:text-lg">
              From a humming idea to a polished export — Beat Box covers the whole loop, no DAW switching required.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                n: "01", t: "Hit record",
                d: "Tap the green dot, beatbox or hum. Phone or laptop, doesn’t matter.",
                img: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?q=80&w=1400&auto=format&fit=crop",
                icon: Microphone,
              },
              {
                n: "02", t: "Stack layers",
                d: "Add rhythm, melody, vocals. Drumify your beatbox into real drums.",
                img: "https://images.unsplash.com/photo-1598653222000-6b7b7a552625?q=80&w=1400&auto=format&fit=crop",
                icon: SlidersHorizontal,
              },
              {
                n: "03", t: "Sound Guy + Export",
                d: "Ask Sound Guy how any tool works, then render to WAV or MP3 and share the link.",
                img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?q=80&w=1400&auto=format&fit=crop",
                icon: Export,
              },
            ].map((s) => (
              <div
                key={s.n}
                className="relative overflow-hidden rounded-3xl border border-[#0A0A0A]/10 p-10 flex flex-col gap-5 min-h-[340px] justify-between text-white group"
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 100%), url("${s.img}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <span className="font-mono text-sm tracking-widest text-white/70 relative z-10">{s.n}</span>
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-full bg-[#A8FF1F] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <s.icon size={22} weight="bold" className="text-[#0A0A0A]" />
                  </div>
                  <h4 className="font-display text-3xl tracking-tighter text-white">{s.t}</h4>
                  <p className="text-white/85 text-sm mt-3 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#C7C9CC]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="mb-16 max-w-3xl text-left">
            <span className="bb-tag">VOICES IN THE BOOTH</span>
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-6">
              Loved by vocalists,<br />producers, beatmakers.
            </h3>
            <p className="text-[#2B2B2B] mt-6 text-base lg:text-lg">
              Early users are using Beat Box to sketch beats on the train, pitch tracks to labels, and turn voice memos into finished demos.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { who: "Casey Hunter", role: "Sonix Production", q: "Drumify turned a humming idea into a polished beat in under two minutes. Wild." },
              { who: "Alex De Mayo", role: "Just a Label", q: "I sketched a hook on my phone walking home and exported a mix before bed." },
              { who: "DJ EchoStorm", role: "BRKN sounds", q: "Sound Guy is genuinely useful — it walks you through every tool without making you feel stupid." },
            ].map((t) => (
              <div key={t.who} className="bb-card p-8 flex flex-col gap-6 min-h-[260px] justify-between">
                <p className="text-[#0A0A0A] text-lg leading-snug">&ldquo;{t.q}&rdquo;</p>
                <div>
                  <div className="font-display text-xl tracking-tight">{t.who}</div>
                  <div className="text-[#6E7378] text-sm font-mono uppercase tracking-widest mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="bg-[#E2E3E5]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="bb-card overflow-hidden bg-[#0A0A0A] text-white border-[#0A0A0A] p-12 lg:p-20 relative">
            <div className="grid grid-cols-12 gap-6 items-end relative z-10">
              <div className="col-span-12 lg:col-span-8">
                <span className="bb-tag border-white/30 text-white/80 bg-white/5">JOIN THE BETA</span>
                <h3 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter mt-6 leading-[0.92]">
                  Your studio<br />is one mic away.
                </h3>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
                <p className="text-white/75 max-w-md text-base">
                  Sign up free, no credit card, no plugins. Make as many beats as your throat can handle.
                </p>
                <Button
                  onClick={() => nav(user ? "/dashboard" : "/register")}
                  data-testid="cta-bottom-signup"
                  className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-14 px-8 text-base font-semibold w-fit"
                >
                  {user ? "Open the studio" : "Claim my studio"} <ArrowRight className="ml-2" size={18} weight="bold" />
                </Button>
              </div>
            </div>
            {/* big watermark */}
            <div aria-hidden className="absolute -bottom-10 -right-10 font-display text-[20vw] lg:text-[16vw] text-white/[0.04] leading-none pointer-events-none">
              BEAT BOX
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#C7C9CC] border-t border-[#B0B3B7]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-display text-2xl tracking-tighter">BEAT BOX</div>
          <div className="flex items-center gap-5 md:gap-8 flex-wrap">
            <Link
              to="/about"
              data-testid="footer-about-link"
              className="text-[#0A0A0A] text-sm font-mono uppercase tracking-widest hover:text-[#A8FF1F] transition-colors"
            >
              About Us
            </Link>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="text-[#0A0A0A] text-sm font-mono uppercase tracking-widest hover:text-[#A8FF1F] transition-colors"
            >
              Contact
            </Link>
            <span className="text-[#6E7378] text-sm font-mono uppercase tracking-widest">
              © 2026 Gruvy Graphics LLC
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
